import { UIFeedbackHooks } from "@/Shared/Types";
import { useAlert, useConfirm, useExportUI, useLightBox, useModal, useToast } from "./TechnicalHooks";

/**
 * =====================================================
 * Hook: useUIFeedback
 * =====================================================
 *
 * Aggregates all UI feedback hooks into a single object.
 *
 * Responsibilities:
 * - Initializes individual feedback hooks:
 *    - Toast notifications (`useToast`)
 *    - Alerts (`useAlert`)
 *    - Confirmation modals (`useConfirm`)
 *    - Generic modals (`useModal`)
 *    - Lightbox (`useLightBox`)
 * - Returns a unified `UIFeedbackHooks` object to be
 *   consumed by a `UIFeedback` component or other orchestration layers.
 * - Simplifies passing multiple hooks down to components.
 *
 * Usage:
 * ```
 * const uiFeedbackHooks = useUIFeedback();
 * <UIFeedback uiFeedbackHooks={uiFeedbackHooks} />
 * ```
 *
 * Author: Samano CASTRE
 * Date: February 18, 2026
 */
export const useUIFeedback = (): UIFeedbackHooks => {
    
    const toastHook = useToast();
    const alertHook = useAlert();
    const confirmHook = useConfirm();
    const modalHook = useModal();
    const lightBoxHook = useLightBox();
    const exportUIHook = useExportUI();

    return {
        toastHook,
        alertHook,
        confirmHook,
        modalHook,
        lightBoxHook,
        exportUIHook
    };
};
